import Modal from 'react-modal';
import {
  FaCheck,
  FaExclamation,
  FaCalendarAlt,
  FaTimes,
  FaClock,
  FaSignInAlt,
  FaSignOutAlt,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaFileAlt,
  FaTrash,
} from 'react-icons/fa';
import { formatDate, formatTime, formatJamKerja } from '../../helper/helper';
import AuthImage from '../common/AuthImage';

const BRAND = '#1B3C33';

/**
 * Modal detail absensi — 1 warna brand (#1B3C33), layout 2 kolom Masuk|Pulang.
 */
const AttendanceDetailModal = ({
  isOpen,
  onClose,
  items = null,
  onDeleteMasuk,
  onDeletePulang,
  onDeleteAll,
}) => {
  const list = Array.isArray(items) ? items : items ? [items] : [];
  const isAdmin = !!(onDeleteMasuk || onDeletePulang || onDeleteAll);

  const renderSide = ({
    title,
    Icon,
    waktu,
    foto,
    ip,
    lokasi,
    onDelete,
  }) => (
    <div className="rounded-lg p-3 border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg shrink-0" style={{ backgroundColor: BRAND }}>
            <Icon className="text-white text-sm" />
          </div>
          <div className="min-w-0">
            <h4 className="font-bold text-sm" style={{ color: BRAND }}>{title}</h4>
            <p className="text-xs text-gray-600">{waktu ? formatTime(waktu) : 'Tidak ada'}</p>
          </div>
        </div>
        {isAdmin && waktu && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shrink-0"
          >
            <FaTrash className="text-[9px]" /> Hapus
          </button>
        )}
      </div>
      {waktu && foto ? (
        <div className="relative w-full rounded-lg overflow-hidden border border-gray-200">
          <AuthImage
            className="w-full h-40 sm:h-48 object-cover"
            src={foto}
            alt={title}
          />
          <div className="absolute top-2 left-2 right-2 flex flex-col items-end gap-1 z-10">
            {ip && (
              <div className="bg-black/40 text-white px-2 py-1 rounded-md text-[10px] flex items-center gap-1">
                <FaNetworkWired className="text-[10px]" />
                <span className="font-semibold">{ip}</span>
              </div>
            )}
            {lokasi && (
              <div className="bg-black/40 text-white px-2 py-1 rounded-md text-[10px] flex items-center gap-1 max-w-full">
                <FaMapMarkerAlt className="text-[10px] shrink-0" />
                <span className="font-semibold truncate">{lokasi}</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400 rounded-lg border border-dashed border-gray-200 bg-white">
          <FaExclamation className="text-2xl mx-auto mb-1" />
          <p className="text-xs">Tidak ada data</p>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Detail Absensi"
      ariaHideApp={false}
      className="fixed inset-0 p-4 flex items-center justify-center z-[60] outline-none"
      overlayClassName="fixed z-[50] inset-0 bg-black/60 backdrop-blur-sm"
      style={{ padding: '1rem', paddingBottom: 'calc(6rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {list.map((item, index) => {
        const jamKerja = item.jumlah_jam_kerja || item.jumlahJamKerja;
        const isHoliday = item.isHoliday;
        const isStatus = !!item.status && !isHoliday;

        return (
          <div
            key={item.idAbsensi || item.id_absensi || `detail-${index}`}
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          >
            {/* Header — solid brand, sama kayak kalender */}
            <div className="relative px-4 py-3" style={{ backgroundColor: BRAND }}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="p-1.5 bg-white/15 rounded-lg shrink-0">
                    {isHoliday || isStatus ? (
                      isHoliday ? <FaCalendarAlt className="text-white text-base" /> : <FaFileAlt className="text-white text-base" />
                    ) : (
                      <FaCheck className="text-white text-base" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-white">
                      {isHoliday ? 'HARI LIBUR' : isStatus ? `STATUS: ${String(item.status).toUpperCase()}` : 'DETAIL ABSENSI'}
                    </h3>
                    <p className="text-xs text-white/85 truncate">
                      {formatDate(item.tanggal)}
                      {!isHoliday && (item.idAbsensi || item.id_absensi) && ` • #${item.idAbsensi || item.id_absensi}`}
                    </p>
                    {!isHoliday && !isStatus && jamKerja ? (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 rounded-full">
                        <FaClock className="text-white text-xs" />
                        <span className="text-white font-semibold text-xs">{formatJamKerja(jamKerja)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 text-white hover:bg-white/15 rounded-full flex-shrink-0"
                  aria-label="Tutup"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isHoliday ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 bg-gray-100">
                    <FaCalendarAlt className="text-2xl" style={{ color: BRAND }} />
                  </div>
                  <h4 className="text-xl font-bold mb-1" style={{ color: BRAND }}>{item.nama}</h4>
                  {item.keterangan && <p className="text-sm text-gray-600 mb-3">{item.keterangan}</p>}
                  {item.is_nasional && (
                    <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                      Hari Libur Nasional
                    </span>
                  )}
                </div>
              ) : !isStatus ? (
                <div className="grid grid-cols-2 gap-3">
                  {renderSide({
                    title: 'Absen Masuk',
                    Icon: FaSignInAlt,
                    waktu: item.waktuMasuk,
                    foto: item.fotoMasuk,
                    ip: item.ipAddressMasuk,
                    lokasi: item.locationNameMasuk,
                    onDelete: onDeleteMasuk ? () => onDeleteMasuk(item) : undefined,
                  })}
                  {renderSide({
                    title: 'Absen Pulang',
                    Icon: FaSignOutAlt,
                    waktu: item.waktuKeluar,
                    foto: item.fotoKeluar,
                    ip: item.ipAddressKeluar,
                    lokasi: item.locationNameKeluar,
                    onDelete: onDeletePulang ? () => onDeletePulang(item) : undefined,
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-3">
                    <FaFileAlt className="text-2xl" style={{ color: BRAND }} />
                  </div>
                  <h4 className="text-lg font-bold mb-1" style={{ color: BRAND }}>{String(item.status).toUpperCase()}</h4>
                  {item.keterangan && <p className="text-sm text-gray-600 mb-3">{item.keterangan}</p>}
                  {item.document && (
                    <div className="mt-3">
                      <AuthImage
                        kind="document"
                        className="rounded-lg mx-auto max-w-full border border-gray-200"
                        src={item.document}
                        alt="Document"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {isAdmin && !isHoliday && (
              <div className="px-4 py-3 border-t border-gray-100 flex flex-col gap-2">
                {!isStatus && onDeleteAll && (
                  <button
                    type="button"
                    onClick={() => onDeleteAll(item)}
                    className="w-full py-2 px-4 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 inline-flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <FaTrash /> Hapus Semua Data Hari Ini
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2 px-4 text-white rounded-lg font-semibold text-sm"
                  style={{ backgroundColor: BRAND }}
                >
                  Tutup
                </button>
              </div>
            )}
          </div>
        );
      })}
    </Modal>
  );
};

export default AttendanceDetailModal;
